/**
 * Reversible card ↔ detail View-Transition morph.
 *
 * Merges the two halves of the harvested technique:
 *
 *   (a) PER-ENTRY view-transition-name + a SHARED view-transition-class.
 *       Each participating element is given a name UNIQUE to its entry so the
 *       browser pairs the outgoing card with the incoming hero automatically, by
 *       entry identity. VT pseudo-elements cannot wildcard dynamic names (there
 *       is no `::view-transition-group(card-morph-*)`), so every participant
 *       also carries a shared `view-transition-class` (declared statically in
 *       resources/css/transitions/_flip.css) — one rule per class then styles
 *       whichever entry is morphing.
 *
 *   (b) Name EXACTLY ONE entry per side, per navigation. Statically naming every
 *       card would snapshot them all as independent groups (expensive, and every
 *       card would animate). Instead this module names the single card the
 *       reader is navigating to/from — at `visit:start` on the outgoing side and
 *       at `content:replace` on the incoming side — for BOTH directions
 *       (list → detail and detail → list). The names are cleared at `visit:end`
 *       so nothing lingers into the next navigation.
 *
 * ── THE PARTS MORPH, NOT THE BOX — 3 Sep 2026 (Morgan's review) ─────────────
 *
 * The first version named the card and the hero as two boxes, and the browser
 * morphed one rectangle into the other while everything inside them crossfaded.
 * On a case study that meant a 16:9 card stretching into a full-bleed band; on a
 * person it meant a portrait card dissolving into a green rectangle, because the
 * "hero" there is the band behind the words and the portrait sits beside it.
 * "The image transition for people is not great — I don't think it's been
 * hooked properly." It had been hooked to the wrong thing.
 *
 * Now each side declares its PARTS with `data-morph-part="image|title|meta"`,
 * and this module names each part `card-morph-<id>-<part>` — so the picture
 * travels to where the picture is, the title to the title, the role or the tags
 * to theirs, each in its own group. A container with no parts falls back to
 * being named whole, which is what the kit's demo pair and any older fork get.
 *
 * ONE PART PER ROLE PER SIDE. Two elements named the same in one document abort
 * the whole transition, so a role that appears twice inside a container is
 * named once (the first). And a part that exists on one side only animates
 * alone — an old-only group fades out, a new-only group fades in — so the two
 * sides of a pair should declare the same set. Both collections do.
 *
 * THE REST OF THE PAGE WAITS. While a morph is in flight the root carries
 * `is-morphing` and `is-morphing--to-detail` / `--to-list`; _flip.css uses the
 * detail-bound one to fade the old page out quickly and hold the new page's
 * unmapped content until the parts have landed, then fade it in. Without that
 * the lead, the meta and the body arrived underneath the moving parts halfway
 * through, which is the "weird overlap" in the brief.
 *
 * Graceful no-op when `document.startViewTransition` is absent: without it Swup
 * runs a plain content swap (its own `native` option collapses to false), so
 * this module assigns no names and the navigation is an ordinary Swup visit.
 *
 * @module transitions/card-morph
 */

/**
 * @typedef {Object} CardMorphConfig
 * @property {string} cardSelector   Listing card element.
 * @property {string} heroSelector   Detail-page morph target (the container the
 *                                   parts live in; named whole only if it has none).
 * @property {string} idAttribute    Attribute carrying the stable per-entry id,
 *                                   present on BOTH the card and the matching hero.
 * @property {string} partAttribute  Attribute marking a morphing part inside either
 *                                   container; its value is the part's role.
 * @property {(id: string) => string} name          Per-entry name for a whole container.
 * @property {(id: string, part: string) => string} partName  Per-entry, per-part name.
 * @property {string} stateClass     Root class while a morph is in flight; the
 *                                   direction is appended as a BEM modifier.
 * @property {((url: string) => boolean) | null} isDetailUrl  Optional matcher that
 *                                   classifies a URL as a detail page. When null,
 *                                   direction is inferred from the DOM (a card
 *                                   click ⇒ to-detail; a hero present ⇒ from-detail)
 *                                   and the incoming side is matched by id, no-op
 *                                   if no partner is found.
 */

/** Sensible kit defaults. Projects override selectors + `isDetailUrl`. */
export const DEFAULTS = {
    cardSelector: '[data-card-morph]',
    heroSelector: '[data-card-morph-hero]',
    idAttribute: 'data-card-id',
    partAttribute: 'data-morph-part',
    name: (id) => `card-morph-${id}`,
    partName: (id, part) => `card-morph-${id}-${part}`,
    stateClass: 'is-morphing',
    isDetailUrl: null,
};

/**
 * Feature test for the native View Transitions API.
 *
 * @returns {boolean}
 */
function supportsViewTransitions() {
    return typeof document !== 'undefined' && typeof document.startViewTransition === 'function';
}

/**
 * Resolve a Swup visit URL (path + search) against the current origin.
 *
 * @param {(string | undefined)} url
 * @returns {(string | null)}
 */
function resolveUrl(url) {
    if (!url) {
        return null;
    }

    return new URL(url, window.location.origin).href;
}

/**
 * Wire the reversible card ↔ detail morph onto a Swup instance.
 *
 * @param {import('swup').default} swup - The Swup instance.
 * @param {Partial<CardMorphConfig>} [options] - Selector/name/matcher overrides.
 * @returns {void}
 */
export function initCardMorph(swup, options = {}) {
    const config = { ...DEFAULTS, ...options };

    /**
     * Elements whose inline `view-transition-name` this module set, so they can
     * be cleared once the transition finishes.
     * @type {HTMLElement[]}
     */
    let named = [];

    /**
     * The morph in flight: which direction and which entry id, carried from
     * `visit:start` (outgoing side named) to `content:replace` (incoming side named).
     * @type {({ direction: 'to-detail' | 'to-list', id: string }) | null}
     */
    let pending = null;

    /**
     * Assign a name to one element and remember it for cleanup.
     * @param {HTMLElement} el
     * @param {string} name
     */
    const assignName = (el, name) => {
        el.style.viewTransitionName = name;
        named.push(el);
    };

    /**
     * Name one side of the pair: each declared part inside the container, once
     * per role, or the container itself when it declares none.
     * @param {HTMLElement} container
     * @param {string} id
     */
    const nameSide = (container, id) => {
        const parts = container.querySelectorAll(`[${config.partAttribute}]`);
        const seen = new Set();

        for (const part of parts) {
            const role = part.getAttribute(config.partAttribute);
            if (!role || seen.has(role)) {
                continue;
            }
            seen.add(role);
            assignName(/** @type {HTMLElement} */ (part), config.partName(id, role));
        }

        if (seen.size === 0) {
            assignName(container, config.name(id));
        }
    };

    /** Clear every name this module assigned. */
    const clearNames = () => {
        for (const el of named) {
            el.style.viewTransitionName = '';
        }
        named = [];
    };

    /**
     * Mark the root for the duration of a morph so the stylesheet can choreograph
     * the page around the moving parts.
     * @param {('to-detail' | 'to-list' | null)} direction
     */
    const setState = (direction) => {
        const root = document.documentElement;
        root.classList.remove(config.stateClass, `${config.stateClass}--to-detail`, `${config.stateClass}--to-list`);
        if (direction) {
            root.classList.add(config.stateClass, `${config.stateClass}--${direction}`);
        }
    };

    /**
     * Find the listing card carrying a given entry id in the current document.
     * @param {string} id
     * @returns {(HTMLElement | null)}
     */
    const findCardById = (id) => {
        const cards = document.querySelectorAll(config.cardSelector);
        for (const card of cards) {
            if (card.getAttribute(config.idAttribute) === id) {
                return /** @type {HTMLElement} */ (card);
            }
        }
        return null;
    };

    /**
     * Whether a URL is (config says) a detail page. With no matcher configured,
     * `whenUnset` is the optimistic assumption used for that direction.
     * @param {(string | undefined)} url
     * @param {boolean} whenUnset
     * @returns {boolean}
     */
    const isDetail = (url, whenUnset) => {
        if (typeof config.isDetailUrl !== 'function') {
            return whenUnset;
        }
        const resolved = resolveUrl(url);
        return resolved ? Boolean(config.isDetailUrl(resolved)) : false;
    };

    // Outgoing side: name exactly one entry BEFORE the swap so it lands in the
    // old view-transition snapshot.
    swup.hooks.on('visit:start', (visit) => {
        // Reset defensively — an aborted prior visit could leave state behind.
        clearNames();
        pending = null;
        setState(null);

        if (!supportsViewTransitions()) {
            return;
        }

        const cardTrigger = visit.trigger?.el?.closest?.(config.cardSelector) ?? null;
        const hero = document.querySelector(config.heroSelector);

        if (cardTrigger && isDetail(visit.to?.url, /* whenUnset */ true)) {
            // list → detail: morph the clicked card's parts.
            const id = cardTrigger.getAttribute(config.idAttribute);
            if (id) {
                nameSide(/** @type {HTMLElement} */ (cardTrigger), id);
                pending = { direction: 'to-detail', id };
            }
        } else if (hero && !isDetail(visit.to?.url, /* whenUnset */ false)) {
            // detail → list: morph the hero's parts back to its listing card.
            const id = hero.getAttribute(config.idAttribute);
            if (id) {
                nameSide(/** @type {HTMLElement} */ (hero), id);
                pending = { direction: 'to-list', id };
            }
        }

        if (pending) {
            setState(pending.direction);
        }
    });

    // Incoming side: name the partner's parts AFTER the swap but still inside the
    // view transition (content:replace runs within startViewTransition in native
    // mode), so they are captured in the new snapshot and the pairs morph.
    swup.hooks.on('content:replace', () => {
        if (!pending) {
            return;
        }

        if (pending.direction === 'to-detail') {
            const hero = document.querySelector(config.heroSelector);
            if (hero && hero.getAttribute(config.idAttribute) === pending.id) {
                nameSide(/** @type {HTMLElement} */ (hero), pending.id);
            }
        } else {
            const card = findCardById(pending.id);
            if (card) {
                nameSide(card, pending.id);
            }
        }
    });

    // Cleanup runs after the transition has finished (visit:end is outside the
    // native VT wrapper) so clearing names never disturbs the running morph.
    swup.hooks.on('visit:end', () => {
        clearNames();
        pending = null;
        setState(null);
    });
}
